import React, { useEffect, useRef, useState, useCallback } from "react";
import { API } from "@/lib/api";
import { MessageSquare, Send, WifiOff } from "lucide-react";
import axios from "axios";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { VoiceBubble } from "@/components/VoiceBubble";
import { useLang } from "@/lib/i18n";

/** Real-time chat block used by the Customer Live View.
 *  - History via REST GET /api/live/{token}/messages (reloaded on WS reconnect
 *    so any messages that arrived during a disconnect gap re-appear without
 *    a page refresh).
 *  - Live via WebSocket /api/ws/chat/customer?token=...
 *  - 20 s application-layer keepalive to survive Kubernetes/Cloudflare idle
 *    proxy timeouts (typically 60–100 s of silence closes idle WSes).
 *  - Chat is CONNECTED throughout LIVE and PAUSED (session stays alive during
 *    pause). Only the SEND button is disabled outside LIVE.
 *  - Sanitized server-side; 500-char cap; auto-reconnect with bounded backoff.
 *  - Deduplicates messages by id so history reload + live broadcast never
 *    produce a duplicate row.
 */
export function ChatBlockCustomer({ token, status, revoked, onSystemMessage }) {
  const { t } = useLang();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const backoffRef = useRef(1000);
  const scrollRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const readOnly = status === "COMPLETED" || revoked;
  // Chat is fully usable (send + receive) during LIVE, PAUSED, and TESTING
  // (Testing phase keeps the session/stream/WS alive; only the SEND input is
  // gated on LIVE|PAUSED via canSend). WAITING is locked, COMPLETED is
  // read-only. Keeping the WS open during TESTING is REQUIRED for realtime
  // testing_state / testing_temp broadcasts to reach the customer.
  const canConnect = (status === "LIVE" || status === "PAUSED" || status === "TESTING") && !revoked;
  const canSend = (status === "LIVE" || status === "PAUSED") && !revoked;

  const mergeMessages = useCallback((incoming) => {
    if (!Array.isArray(incoming) || incoming.length === 0) return;
    setMessages((prev) => {
      const byId = new Map(prev.map((m) => [m.id, m]));
      for (const m of incoming) if (!byId.has(m.id)) byId.set(m.id, m);
      const merged = Array.from(byId.values());
      merged.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      return merged;
    });
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/live/${token}/messages`);
      mergeMessages(data.messages || []);
    } catch {}
  }, [token, mergeMessages]);

  useEffect(() => { loadHistory(); }, [loadHistory, status]);

  useEffect(() => {
    if (!canConnect) return;
    let closed = false;
    const clearPing = () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
    const connect = () => {
      const base = API.replace(/^http/, "ws").replace(/\/api$/, "");
      const ws = new WebSocket(`${base}/api/ws/chat/customer?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onopen = () => {
        setConnected(true);
        backoffRef.current = 1000;
        // Reload history on every successful (re)connect so messages missed
        // during a disconnect gap re-appear without a page refresh.
        loadHistory();
        // Application-layer keepalive to defeat proxy idle timeouts.
        clearPing();
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === 1) {
            try { ws.send(JSON.stringify({ type: "ping" })); } catch {}
          }
        }, 20000);
      };
      ws.onmessage = (evt) => {
        try {
          const p = JSON.parse(evt.data);
          if (p.type === "message") {
            mergeMessages([p.message]);
          } else if (onSystemMessage && (p.type === "testing_state" || p.type === "testing_temp" || p.type === "hello")) {
            onSystemMessage(p);
          }
        } catch {}
      };
      ws.onclose = () => {
        setConnected(false);
        clearPing();
        if (!closed && canConnect) {
          setTimeout(connect, backoffRef.current);
          backoffRef.current = Math.min(backoffRef.current * 2, 15000);
        }
      };
      ws.onerror = () => ws.close();
    };
    connect();
    return () => {
      closed = true;
      clearPing();
      try { wsRef.current?.close(); } catch {}
    };
  }, [token, canConnect, loadHistory, mergeMessages]);

  useEffect(() => {
    try { scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); } catch {}
  }, [messages]);

  const send = (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || !canSend || !wsRef.current || wsRef.current.readyState !== 1) return;
    wsRef.current.send(JSON.stringify({ type: "message", text: t, temp_id: Date.now() }));
    setText("");
  };

  const sendVoice = useCallback(async (blob, duration, mimeType) => {
    const form = new FormData();
    const ext = (mimeType && mimeType.split("/")[1]?.split(";")[0]) || "webm";
    form.append("audio", blob, `voice-${Date.now()}.${ext}`);
    form.append("duration", String(duration));
    const { data } = await axios.post(`${API}/live/${token}/voice`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    if (data?.message) mergeMessages([data.message]);
  }, [token, mergeMessages]);

  return (
    <div className="rounded-xl border p-5 md:p-6" style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0d0d10" }} data-testid="chat-section">
      <div className="flex flex-col md:flex-row md:items-start gap-5 md:gap-8">
        <div className="flex items-start gap-3 md:min-w-[280px]">
          <span className="w-9 h-9 rounded-md border flex items-center justify-center shrink-0" style={{ borderColor: "rgba(0,229,255,0.35)", background: "rgba(0,229,255,0.05)", color: "var(--accent)" }}>
            <MessageSquare size={16} />
          </span>
          <div>
            <div className="text-sm md:text-base font-semibold tracking-tight text-white">{t("chat_title")}</div>
            <div className="mt-1 text-[12px] md:text-[13px] text-white/50 leading-snug">
              {readOnly ? t("chat_session_ended") : status === "WAITING" ? t("chat_will_open") : t("chat_send_note")}
            </div>
            {canConnect && !connected && (
              <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-white/40"><WifiOff size={11}/> {t("chat_reconnecting")}</div>
            )}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div ref={scrollRef} className="max-h-64 md:max-h-72 overflow-y-auto pr-1 mb-3" data-testid="chat-history">
            {messages.length === 0 ? (
              <div className="text-white/30 text-xs italic py-6 text-center">{t("chat_no_messages")}</div>
            ) : messages.map((m) => (
              <div key={m.id} className={`flex mb-2 ${m.sender_role === "customer" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.sender_role}`}>
                <div className="max-w-[80%] rounded-lg px-3 py-2 text-[13px]" style={{
                  background: m.sender_role === "customer" ? "rgba(0,229,255,0.14)" : "rgba(255,255,255,0.04)",
                  border: "1px solid " + (m.sender_role === "customer" ? "rgba(0,229,255,0.25)" : "rgba(255,255,255,0.08)"),
                  color: "#fff",
                }}>
                  {m.kind === "voice" ? (
                    <VoiceBubble
                      audioUrl={m.audio_url}
                      duration={m.duration_seconds}
                      contentType={m.audio_content_type}
                      authQuery={`?token=${encodeURIComponent(token)}`}
                      testid={`voice-msg-${m.sender_role}`}
                    />
                  ) : (
                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  )}
                  <div className="mt-1 text-[10px] text-white/40">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="flex items-center gap-3 min-w-0">
            <div className="relative flex-1 min-w-0 flex items-center rounded-lg border h-11 pr-2" style={{ borderColor: "rgba(255,255,255,0.1)", background: "#050507", opacity: canSend ? 1 : 0.55 }}>
              <input type="text" value={text} maxLength={500} disabled={!canSend} onChange={(e) => setText(e.target.value)} placeholder={canSend ? t("chat_type_message") : readOnly ? t("chat_placeholder_ended") : t("chat_placeholder_waiting")} className="flex-1 min-w-0 bg-transparent px-4 h-full text-sm text-white placeholder-white/30 focus:outline-none" data-testid="chat-input" />
              <VoiceRecorder disabled={!canSend} onSend={sendVoice} size={34} />
            </div>
            <button type="submit" disabled={!canSend || !text.trim()} className="inline-flex items-center gap-2 h-11 px-5 rounded-lg font-semibold text-[13px] transition-transform hover:-translate-y-[1px] disabled:opacity-40" style={{ background: "var(--accent)", color: "#000" }} data-testid="chat-send"><Send size={14} /> {t("chat_send")}</button>
          </form>
        </div>
      </div>
    </div>
  );
}
